import React from "react";
import { observer } from "mobx-react-lite";
import { appStore } from "@/app_store";
import { Clickable } from "@/components/toolkit/Clickable";
import { TextInput } from "@/components/toolkit/TextInput";
import { ConfirmDialog } from "@/components/toolkit/ConfirmDialog";
import { t } from "@/i18n";

// Private keys are often extensionless (id_rsa, id_ed25519), so the leading
// bare-name hints keep those visible alongside the common encoded formats.
// .ppk (PuTTY) is intentionally excluded -- ssh -i can't read it, it fails with
// "error in libcrypto". Users must convert to OpenSSH/PEM format first.
const PRIVATE_KEY_ACCEPT = "id_rsa,id_ed25519,id_ecdsa,id_dsa,.pem,.key,.openssh";
const PUBLIC_KEY_ACCEPT = ".pub";

// A single key field: upload a file, or flip the toggle to type/paste manually.
// The two stay in sync -- choosing a file fills the value (and the textarea, if
// expanded); editing the text away from the uploaded content drops the
// "file chosen" label, since it's no longer that file.
const KeyField = observer(function KeyField(props: {
    label: string;
    placeholder: string;
    accept: string;
    value: string;
    onChange: (value: string) => void;
}) {
    const fileRef = React.useRef<HTMLInputElement>(null);
    const [expanded, setExpanded] = React.useState(false);
    const [fileName, setFileName] = React.useState<string | null>(null);
    const loadedRef = React.useRef<string | null>(null);

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // let the same file be re-picked later
        if (!file) return;
        const content = await file.text();
        loadedRef.current = content;
        setFileName(file.name);
        props.onChange(content);
    };

    const handleManualChange = (value: string) => {
        if (fileName !== null && value !== loadedRef.current) {
            setFileName(null);
            loadedRef.current = null;
        }
        props.onChange(value);
    };

    return (
        <div className="activity_bar__settings_section">
            <div className="activity_bar__settings_section_title">{props.label}</div>
            <div className="activity_bar__ssh_key_input_row">
                <Clickable onClick={() => fileRef.current?.click()}>
                    <button className="activity_bar__dialog_btn activity_bar__ssh_upload_btn">
                        {fileName ?? t("ssh_keys_upload")}
                    </button>
                </Clickable>
                <input
                    ref={fileRef}
                    type="file"
                    accept={props.accept}
                    className="activity_bar__ssh_file_input"
                    onChange={handleFile}
                />
                <label className="activity_bar__ssh_manual_toggle">
                    <input type="checkbox" checked={expanded} onChange={() => setExpanded(v => !v)} />
                    <span>{t("ssh_keys_enter_manually")}</span>
                </label>
            </div>
            {expanded && (
                <TextInput value={props.value} placeholder={props.placeholder} isTextArea onChange={handleManualChange} />
            )}
        </div>
    );
});

export const SSHKeysSettings = observer(function SSHKeysSettings() {
    const catalog = appStore.agent.session?.ssh_keys ?? null;
    const keys = catalog ? Object.values(catalog.keys) : [];

    // use_key / active_key live on repository containers (IRepository); the sandbox
    // container has neither, so feature-detect before offering the action.
    const container = appStore.agent.session?.container as
        | { use_key?: (name: string) => Promise<void>; active_key?: string | null }
        | null;
    const canUseKey = typeof container?.use_key === "function";

    // Derived from the backend (inferred from git's ssh config), not local state.
    const activeKeyId = container?.active_key ?? null;

    const useKey = async (id: string) => {
        if (!canUseKey) return;
        await container!.use_key!(id);
    };

    const [isAdding, setIsAdding] = React.useState(false);
    const [confirmRemove, setConfirmRemove] = React.useState<{ id: string; name: string } | null>(null);
    const [name, setName] = React.useState("");
    const [privateKey, setPrivateKey] = React.useState("");
    const [publicKey, setPublicKey] = React.useState("");

    const resetForm = () => {
        setName("");
        setPrivateKey("");
        setPublicKey("");
        setIsAdding(false);
    };

    const addKey = async () => {
        if (!catalog || !privateKey.trim()) return;
        await catalog.add_key(name, privateKey, publicKey);
        resetForm();
    };

    const removeKey = async () => {
        if (!catalog || !confirmRemove) return;
        await catalog.remove_key(confirmRemove.id);
        setConfirmRemove(null);
    };

    return (
        <>
            <div className="activity_bar__settings_section">
                <div className="activity_bar__ssh_keys_list">
                    {keys.length === 0 ? (
                        <div className="activity_bar__ssh_keys_empty">{t("ssh_keys_empty")}</div>
                    ) : keys.map(key => {
                        const isActive = key.id === activeKeyId;
                        return (
                            <div
                                key={key.id}
                                className={`activity_bar__ssh_key_item${isActive ? " activity_bar__ssh_key_item--active" : ""}`}
                            >
                                <div className="activity_bar__ssh_key_info">
                                    <span className="activity_bar__ssh_key_name">{key.name.trim() || key.id}</span>
                                </div>
                                <div className="activity_bar__ssh_key_actions">
                                    {isActive ? (
                                        <span className="activity_bar__ssh_key_active">{t("ssh_keys_active")}</span>
                                    ) : (
                                        <Clickable onClick={() => useKey(key.id)}>
                                            <button className="activity_bar__ssh_key_use" disabled={!canUseKey}>
                                                {t("ssh_keys_use")}
                                            </button>
                                        </Clickable>
                                    )}
                                    <Clickable onClick={() => setConfirmRemove({ id: key.id, name: key.name.trim() || key.id })}>
                                        <button className="activity_bar__ssh_key_remove">{t("ssh_keys_remove")}</button>
                                    </Clickable>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {!isAdding ? (
                <div className="activity_bar__settings_section">
                    <Clickable onClick={() => setIsAdding(true)}>
                        <button
                            className="activity_bar__dialog_btn activity_bar__dialog_btn--create"
                            disabled={!catalog}
                        >
                            {t("ssh_keys_add_new")}
                        </button>
                    </Clickable>
                </div>
            ) : (
                <>
                    <div className="activity_bar__ssh_form_header">{t("ssh_keys_new_title")}</div>
                    <div className="activity_bar__settings_section">
                        <div className="activity_bar__settings_section_title">{t("ssh_keys_name_label")}</div>
                        <TextInput value={name} placeholder={t("ssh_keys_name_placeholder")} onChange={setName} />
                    </div>
                    <KeyField
                        label={t("ssh_keys_private_label")}
                        placeholder={t("ssh_keys_private_placeholder")}
                        accept={PRIVATE_KEY_ACCEPT}
                        value={privateKey}
                        onChange={setPrivateKey}
                    />
                    <KeyField
                        label={t("ssh_keys_public_label")}
                        placeholder={t("ssh_keys_public_placeholder")}
                        accept={PUBLIC_KEY_ACCEPT}
                        value={publicKey}
                        onChange={setPublicKey}
                    />
                    <div className="activity_bar__dialog_actions">
                        <Clickable onClick={resetForm}>
                            <button className="activity_bar__dialog_btn activity_bar__dialog_btn--cancel">
                                {t("ssh_keys_cancel")}
                            </button>
                        </Clickable>
                        <Clickable onClick={addKey}>
                            <button
                                className="activity_bar__dialog_btn activity_bar__dialog_btn--create"
                                disabled={!catalog || !privateKey.trim()}
                            >
                                {t("ssh_keys_add")}
                            </button>
                        </Clickable>
                    </div>
                </>
            )}

            {confirmRemove && (
                <ConfirmDialog
                    title={t("ssh_keys_remove_title")}
                    message={t("ssh_keys_remove_confirm").replace("{name}", confirmRemove.name)}
                    confirmLabel={t("ssh_keys_remove")}
                    danger
                    onConfirm={removeKey}
                    onCancel={() => setConfirmRemove(null)}
                />
            )}
        </>
    );
});
